import { useState, useEffect } from 'react';
import { supabase, type NewsItem, type Category } from '@/lib/supabase';

export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('categories').select('*').order('name').then(({ data }) => {
      setCategories(data || []);
      setLoading(false);
    });
  }, []);

  return { categories, loading };
}

export function useAllNews(limit?: number) {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let query = supabase.from('news').select('*').order('date', { ascending: false });
    if (limit) query = query.limit(limit);
    query.then(({ data }) => {
      setNews(data || []);
      setLoading(false);
    });
  }, [limit]);

  return { news, loading };
}

export function useFeaturedNews() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('news').select('*').eq('featured', true).order('date', { ascending: false }).then(({ data }) => {
      setNews(data || []);
      setLoading(false);
    });
  }, []);

  return { news, loading };
}

export function useNewsByCategory(categoryId: string) {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!categoryId) return;
    supabase.from('news').select('*').eq('category', categoryId).order('date', { ascending: false }).then(({ data }) => {
      setNews(data || []);
      setLoading(false);
    });
  }, [categoryId]);

  return { news, loading };
}

export function useNewsById(idOrSlug: string) {
  const [newsItem, setNewsItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!idOrSlug) return;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrSlug);
    const fetch = async () => {
      setLoading(true);
      if (isUUID) {
        const { data } = await supabase.from('news').select('*').eq('id', idOrSlug).maybeSingle();
        setNewsItem(data);
      } else {
        // slug ile ara
        const { data } = await supabase.from('news').select('*').eq('slug', idOrSlug).maybeSingle();
        if (data) {
          setNewsItem(data);
        } else {
          // fallback: title içinde ara
          const { data: fallback } = await supabase.from('news').select('*').ilike('slug', `${idOrSlug}%`).limit(1).maybeSingle();
          setNewsItem(fallback);
        }
      }
      setLoading(false);
    };
    fetch();
  }, [idOrSlug]);

  return { newsItem, loading };
}

export function usePopularNews(limit = 10) {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('news').select('*').order('views', { ascending: false }).limit(limit).then(({ data }) => {
      setNews(data || []);
      setLoading(false);
    });
  }, [limit]);

  return { news, loading };
}

export function useSearchNews(query: string) {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) { setNews([]); return; }
    setLoading(true);
    supabase.from('news').select('*').or(`title.ilike.%${query}%,excerpt.ilike.%${query}%`).order('date', { ascending: false }).then(({ data }) => {
      setNews(data || []);
      setLoading(false);
    });
  }, [query]);

  return { news, loading };
}

/**
 * Smart related news: same category + keyword overlap + popularity boost
 * Returns up to `limit` items, excluding the current article.
 */
export function useSmartRelatedNews(currentId: string, category: string, title: string, limit = 6) {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentId || !category) return;

    const fetchRelated = async () => {
      setLoading(true);

      // 1. Same category (up to 20 candidates)
      const { data: sameCat } = await supabase
        .from('news')
        .select('*')
        .eq('category', category)
        .neq('id', currentId)
        .order('date', { ascending: false })
        .limit(20);

      // 2. Extract keywords from title (words > 3 chars)
      const keywords = title
        .toLowerCase()
        .replace(/[^a-zçğıöşü\s]/gi, '')
        .split(/\s+/)
        .filter(w => w.length > 3);

      // 3. Score each candidate
      const scored = (sameCat || []).map(item => {
        let score = 0;
        const itemText = `${item.title} ${item.excerpt}`.toLowerCase();

        // Keyword overlap
        keywords.forEach(kw => {
          if (itemText.includes(kw)) score += 3;
        });

        // Popularity boost (views)
        if (item.views > 10000) score += 4;
        else if (item.views > 5000) score += 2;
        else if (item.views > 1000) score += 1;

        // Recency boost (within 30 days)
        const daysDiff = (Date.now() - new Date(item.date).getTime()) / (1000 * 60 * 60 * 24);
        if (daysDiff < 7) score += 3;
        else if (daysDiff < 30) score += 1;

        return { ...item, _score: score };
      });

      // 4. Sort by score desc, then by views
      scored.sort((a, b) => b._score - a._score || (b.views || 0) - (a.views || 0));

      // 5. If not enough from same category, fill with popular from other categories
      let result = scored.slice(0, limit);

      if (result.length < limit) {
        const needed = limit - result.length;
        const existingIds = new Set([currentId, ...result.map(n => n.id)]);
        const { data: popular } = await supabase
          .from('news')
          .select('*')
          .neq('id', currentId)
          .neq('category', category)
          .order('views', { ascending: false })
          .limit(needed + 5);

        const extra = (popular || [])
          .filter(n => !existingIds.has(n.id))
          .slice(0, needed);
        result = [...result, ...extra];
      }

      setNews(result.slice(0, limit));
      setLoading(false);
    };

    fetchRelated();
  }, [currentId, category, title, limit]);

  return { news, loading };
}
